import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { IQuoteListParams, QUOTE_CONTACT_NAME_SORT_COLUMN } from "shared";

/** Maps grid column fields to the DB sort column understood by the backend */
export const quoteSortColumnMap: Record<string, string> = {
  issueDateString: `issue_date_str`,
  expiryDateString: `expiry_date_str`,
  number: `number`,
  contactName: QUOTE_CONTACT_NAME_SORT_COLUMN,
  totalAmountFormatted: `total_amount_cents`,
  customerDataPONumber: `customer_data_po_number`,
};

interface QuotePaginationInput {
  page: number;
  pageSize: number;
}

function toDateOnlyString(value: unknown) {
  if (!value || typeof value !== `string`) {
    return null;
  }

  return value.split(`T`)[0] || null;
}

function toNullableNumber(value: unknown) {
  if (value === undefined || value === null || value === ``) {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
}

export function buildQuoteListParamsFromModels(
  filterModel: GridFilterModel,
  sortModel: GridSortModel | undefined,
  pagination: QuotePaginationInput | undefined,
  status: string,
): IQuoteListParams {
  const page = pagination ? pagination.page + 1 : 1;
  const limit = pagination?.pageSize ?? 100;

  const number = filterModel.items.find(item => item.field === `number`)?.value || null;
  const contactName = filterModel.items.find(item => item.field === `contactName`)?.value || null;
  const poNumber = filterModel.items.find(item => item.field === `customerDataPONumber`)?.value || null;

  const minIssueDate = filterModel.items.find(item => item.field === `issueDateString` && item.operator === `onOrAfter`)?.value || null;
  const maxIssueDate = filterModel.items.find(item => item.field === `issueDateString` && item.operator === `onOrBefore`)?.value || null;
  const minExpiryDate = filterModel.items.find(item => item.field === `expiryDateString` && item.operator === `onOrAfter`)?.value || null;
  const maxExpiryDate = filterModel.items.find(item => item.field === `expiryDateString` && item.operator === `onOrBefore`)?.value || null;

  const totalAmountItem = filterModel.items.find(item => item.field === `totalAmountFormatted`);
  const totalAmount = toNullableNumber(totalAmountItem?.value);
  const totalAmountOperator = totalAmountItem?.operator || null;

  const quickSearchStr = filterModel.quickFilterValues?.[0] || null;

  let sortColumn = sortModel?.[0]?.field || null;
  const sortOrder = sortModel?.[0]?.sort || null;

  if (sortColumn && sortColumn in quoteSortColumnMap) {
    sortColumn = quoteSortColumnMap[sortColumn];
  }

  return {
    page,
    limit,
    status,
    sortColumn,
    sortType: sortOrder,
    quoteTextSearch: quickSearchStr,
    number,
    contactName,
    poNumber,
    issueDateMinDate: toDateOnlyString(minIssueDate),
    issueDateMaxDate: toDateOnlyString(maxIssueDate),
    expiryDateMinDate: toDateOnlyString(minExpiryDate),
    expiryDateMaxDate: toDateOnlyString(maxExpiryDate),
    totalAmount,
    totalAmountOperator: totalAmountOperator as `>=` | `<=` | `=` | null,
  };
}
